import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export default function Events() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const { data: events } = useQuery({
    queryKey: ['public-events'],
    queryFn: async () => {
      const { data } = await supabase.from('events' as any)
        .select('*').eq('is_published', true).order('starts_at');
      return (data as any[]) || [];
    },
  });

  const { data: bookings } = useQuery({
    queryKey: ['my-event-bookings', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase.from('event_bookings' as any)
        .select('*, events(title, starts_at)').eq('user_id', user!.id);
      return (data as any[]) || [];
    },
  });

  const book = async (eventId: string) => {
    const { error } = await supabase.from('event_bookings' as any).insert({
      event_id: eventId, user_id: user!.id, status: 'reserved',
    });
    if (error) return toast.error(error.message);
    toast.success('Booth reserved — pay invoice to confirm.');
    qc.invalidateQueries({ queryKey: ['my-event-bookings'] });
  };

  const cancel = async (id: string) => {
    await supabase.from('event_bookings' as any).update({ status: 'cancelled' }).eq('id', id);
    toast.success('Booking cancelled');
    qc.invalidateQueries({ queryKey: ['my-event-bookings'] });
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Calendar className="h-6 w-6 text-primary" /> Events & Expos
        </h1>
        <p className="text-sm text-muted-foreground">Book a booth at upcoming real-estate events.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {events?.map((e) => (
          <Card key={e.id}>
            <CardContent className="p-5 space-y-2">
              <h3 className="font-semibold text-lg">{e.title}</h3>
              <p className="text-sm text-muted-foreground">{e.description}</p>
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {format(new Date(e.starts_at), 'd MMM yyyy')}</span>
                {e.city && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {e.city}</span>}
              </div>
              <p className="text-sm font-bold">{Number(e.price_etb_per_booth).toLocaleString()} ETB / booth</p>
              <Button size="sm" onClick={() => book(e.id)} disabled={!user}>Book a booth</Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">My bookings</CardTitle></CardHeader>
        <CardContent>
          {!bookings?.length ? <p className="text-sm text-muted-foreground py-6 text-center">No bookings yet.</p> : (
            <div className="divide-y">
              {bookings.map((b) => (
                <div key={b.id} className="py-3 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{b.events?.title}</p>
                    <p className="text-xs text-muted-foreground">{b.events?.starts_at && format(new Date(b.events.starts_at), 'd MMM yyyy')}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={b.status === 'paid' ? 'default' : b.status === 'cancelled' ? 'outline' : 'secondary'} className="capitalize">{b.status}</Badge>
                    {b.status !== 'cancelled' && <Button size="sm" variant="outline" onClick={() => cancel(b.id)}>Cancel</Button>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
